import React from 'react';

import { useSnackbar } from 'notistack';

import { Typography } from '@mui/material';

import { useRefreshPropertyContentCache } from '~/hooks/apis/accommodation/usePropertyContentCache';

import { RefreshedPropertyReviews } from '~/services/AccommodationService';

import PropertyActionCard from './PropertyActionCard';

interface Props {
  propertyId: string;
}

function countRefreshed(entries: Array<RefreshedPropertyReviews>): number {
  return entries.filter((entry) => entry.refreshed).length;
}

export default function PropertyContentCacheAction({ propertyId }: Props) {
  const { enqueueSnackbar } = useSnackbar();
  const refreshMutation = useRefreshPropertyContentCache();

  const onRefresh = () => {
    refreshMutation.mutate(
      { propertyId },
      {
        onSuccess: (result) =>
          enqueueSnackbar(
            `Content cache regenerated for ${countRefreshed(result)} region, brand and currency combinations`,
            { variant: 'success' },
          ),
      },
    );
  };

  const refreshedCount = refreshMutation.data ? countRefreshed(refreshMutation.data) : 0;

  return (
    <PropertyActionCard
      title="Content cache"
      description="Rebuild the cached property content for every region, brand and currency combination already cached. Combinations with no cache entry are left alone - the next request builds them fresh."
      buttonLabel="Regenerate content cache"
      onClick={onRefresh}
      isLoading={refreshMutation.isPending}
      disabled={!propertyId}
      errorMessage={refreshMutation.error?.message}
    >
      {refreshMutation.isSuccess && (
        <Typography variant="body2">
          {refreshedCount
            ? `Regenerated ${refreshedCount} cached combinations.`
            : 'Nothing was cached for this property, so there was nothing to regenerate.'}
        </Typography>
      )}
    </PropertyActionCard>
  );
}
